import { type ErrorComponentProps } from '@tanstack/react-router'
import { Component, type ReactNode } from 'react'

import { captureException } from '@/lib/sentry'

import { ErrorState } from './state-error'

interface SectionBoundaryProps {
  children: ReactNode
  // When any value here changes the boundary clears its error and re-renders —
  // pass the active filters so changing them recovers from a failed section.
  resetKeys?: unknown[]
  title?: string
  message?: string
  // Rendered instead of the default ErrorState on a caught error. Use for small
  // chrome slots (e.g. the header shop selector) that should degrade silently
  // rather than show a full error panel.
  fallback?: ReactNode
}

interface SectionBoundaryState {
  hasError: boolean
}

// Contains a render error to this subtree: instead of the error bubbling to the
// root boundary (which blanks the whole app), it shows a localized, retryable
// ErrorState so the rest of the page keeps working. Pair with QueryState —
// QueryState handles the expected loading/empty/error data states; this only
// catches the unexpected throws (e.g. a payload whose shape drifted).
export class SectionBoundary extends Component<SectionBoundaryProps, SectionBoundaryState> {
  state: SectionBoundaryState = { hasError: false }

  static getDerivedStateFromError(): SectionBoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: unknown) {
    captureException(error)
  }

  componentDidUpdate(prevProps: SectionBoundaryProps) {
    if (this.state.hasError && !keysEqual(prevProps.resetKeys, this.props.resetKeys)) {
      this.setState({ hasError: false })
    }
  }

  reset = () => {
    this.setState({ hasError: false })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback !== undefined) {
        return this.props.fallback
      }
      return (
        <ErrorState title={this.props.title} message={this.props.message} onRetry={this.reset} />
      )
    }
    return this.props.children
  }
}

function keysEqual(a: unknown[] | undefined, b: unknown[] | undefined): boolean {
  if (a === b) return true
  if (!a || a.length !== b?.length) return false
  return a.every((value, i) => Object.is(value, b[i]))
}

// Route-level fallback for TanStack Router's `errorComponent`. Catches anything
// thrown while rendering a route (including derived values computed in the
// component body, which a boundary placed *inside* the page cannot catch), so a
// crash is contained to the page body while the app shell/nav stays alive.
export function RouteErrorState({ reset }: ErrorComponentProps) {
  return (
    <div className="p-4 md:p-6 lg:p-8">
      <ErrorState onRetry={reset} />
    </div>
  )
}
